import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Zap, TrendingUp, DollarSign, Clock, 
  FileText, ChevronRight, ArrowRight, BarChart3, 
  Lock, Globe, Search, Plus, Minus, CreditCard, Wallet,
  Building2, Landmark, ChevronDown, MessageSquare, CheckCircle
} from 'lucide-react';

import { SelfManagedHosting } from '../components/sections/SelfManagedHosting';
import { FaqSection } from '../components/sections/FaqSection';

interface HostLandingPageProps {
  navigateTo: (page: string, data?: any) => void;
  onJoinWaitingList: () => void;
}

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export const HostLandingPage = ({ navigateTo, onJoinWaitingList }: HostLandingPageProps) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [rentAmount, setRentAmount] = useState<number>(10000);
  const [savings, setSavings] = useState<{ coleese: number, traditional: number, difference: number }>({ coleese: 0, traditional: 0, difference: 0 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const plans = {
    monthly: { name: "Host Monthly", price: 149, planCode: "PLN_yhthkf53dmaa828", description: "Hosting fee billed monthly.", features: ["Unlimited Listings", "Tenant Screening", "Lease Management", "Online Payments", "Priority Support"] },
    yearly: { name: "Host Yearly", price: 119, totalAnnualPrice: 1430.40, planCode: "PLN_rzv084lidj9ur3e", description: "Save 20% on hosting fees.", badge: "SAVE 20%", features: ["Unlimited Listings", "Tenant Screening", "Lease Management", "Online Payments", "Priority Support", "AI Insights"] },
  };

  const selectedPlan = plans[billingCycle as keyof typeof plans];

  useEffect(() => {
    // Coleese: R149/mo platform + R399/mo management = R449/mo
    const coleeseAnnual = 549 * 12;
    
    // Traditional: 1 month success fee (rentAmount) + 8% monthly management + R1000 renewal
    const traditionalAnnual = rentAmount + (rentAmount * 0.08 * 12) + 1000;
    
    setSavings({
      coleese: coleeseAnnual,
      traditional: traditionalAnnual,
      difference: traditionalAnnual - coleeseAnnual
    });
  }, [rentAmount]);

  return (
    <div className="min-h-screen bg-white text-[#191C1F] selection:bg-blue-500/30 font-sans overflow-x-hidden">
      {/* 
        HERO SECTION - Exact Revolut Layout 
      */}
      <section className="relative overflow-hidden">
        <div className="container relative z-10 pt-5 pb-24 lg:py-48 px-6 mx-auto flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 text-center lg:text-left">
          <p className="text-[#646A71] text-sm lg:text-lg font-medium mb-4">Self Managed Hosting</p>
          <motion.h1 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-[40px] lg:text-[84px] font-black tracking-[-0.04em] leading-[1.2] lg:leading-[1] mb-6"
          >
            Hosting: ready <br />
            for takeoff
          </motion.h1>
          <p className="text-base lg:text-lg text-[#646A71] mb-10 max-w-xl leading-relaxed lg:mx-0 mx-auto">
            Your property is going places. Keep more of its income, with zero commission self management tools.
          </p>
          <button 
            onClick={onJoinWaitingList}
            className="px-8 py-4 rounded-full bg-[#191C1F] text-white font-bold text-sm lg:text-base hover:opacity-90 transition-all active:scale-95"
          >
            Get access
          </button>
        </div>

        {/* Exchange-style Calculator Card */}
        <div className="flex-1 w-full flex justify-center lg:justify-start relative text-center">
          <div className="absolute right-[-20%] top-[40%] lg:right-[-20%] lg:top-[-30%] w-[400px] h-[600px] lg:w-[800px] lg:h-[800px] bg-[#F2F4F7] rounded-[24px] -z-10 overflow-hidden">
             <div className="w-full h-full border-[1px] border-white/40 rounded-full scale-[1.1]">
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2Faeff2ce247e45199663b6c2423c76a6f.jpg?alt=media&token=2df9d646-d30d-4c17-a054-11a34aaeb1bf" 
                  alt="Productive Host" 
                  className="w-full h-full object-cover object-[55%_center] lg:object-[70%_center]"
                  referrerPolicy="no-referrer"
                />
             </div>
             <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 40, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="w-full max-w-[400px] bg-black/40 backdrop-blur-xl rounded-[24px] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] p-6 border border-white/10"
          >
            <div className="mb-2">
              <p className="text-[11px] text-white font-bold uppercase tracking-wide mb-1">What Is Your Monthly Rent?</p>
              <div className="rounded-xl p-4 flex items-center justify-center bg-white rounded-2xl border-b border-[#E9ECEF] hover:border-black/10 transition-colors">
                <input 
                  type="text" 
                  value={`R ${rentAmount.toLocaleString()}`}
                  onChange={(e) => {
                    const val = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                    setRentAmount(val);
                  }}
                  className="bg-transparent text-3xl font-black w-full outline-none tracking-tight text-center"
                />
              </div>
            </div>

            <div className="space-y-4 relative mb-4 bg-white rounded-2xl py-4">
              <div className="flex items-center justify-center gap-3 px-2">
                <div className="w-4 h-4 rounded-full bg-[#E9ECEF] flex items-center justify-center">
                   <Minus size={10} className="text-[#646A71]" />
                </div>
                <div className="text-[12px] text-[#646A71]">
                  <span className="font-bold">R 399/pm</span> <span className="text-[#646A71] opacity-60">Platform fee per lease</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3 px-2">
                <div className="w-4 h-4 rounded-full bg-[#E9ECEF] flex items-center justify-center">
                   <Minus size={10} className="text-[#646A71]" />
                </div>
                <div className="text-[12px] text-[#646A71]">
                  <span className="font-bold">R 149/pm</span> <span className="text-[#646A71] opacity-60">Platform Access fee</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[11px] text-white font-bold uppercase tracking-wide mb-1">Extra Earnings Annually</p>
              <div className="bg-white text-black rounded-xl p-4 flex items-center justify-center shadow-lg">
                <div className="text-3xl font-black tracking-tight">R {Math.floor(savings.difference).toLocaleString()}</div>
              </div>
            </div>

            <button 
              onClick={() => navigateTo('dashboard')}
              className="w-full py-4 mt-4 bg-[#191C1F] text-white rounded-full font-bold hover:opacity-90 transition-all shadow-xl active:scale-95"
            >
              Get started now
            </button>

            <p className="mt-4 text-[10px] text-white text-center opacity-60 leading-tight px-4">
              Fees are inclusive of listing priority. Annual savings based on 8% management vs Coleese flat platform fee model.
            </p>

            <div className="mt-4 pt-4 border-t border-[#E9ECEF] flex items-center justify-center gap-2 text-[11px] text-lime-400 font-bold">
               <Zap size={12} /> Savings should increase by next cycle
            </div>
          </motion.div>
        </div>
      </div>
    </section>

      {/* 
        REVENUE & EXPENSES SECTION 
      */}
      <section className="py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] lg:text-[64px] font-black tracking-tight leading-none mb-6"
          >
            Unified network control
          </motion.h2>
          <p className="text-[#646A71] text-base lg:text-xl font-medium mb-12 max-w-3xl mx-auto">
            One platform for your entire portfolio, anywhere in South Africa.
          </p>
          
          <div className="relative rounded-[24px] overflow-hidden group shadow-2xl">
            <img 
                src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2FGemini_Generated_Image_20x0nj20x0nj20x0-Photoroom.png?alt=media&token=7f81f483-5733-4bd7-8ca8-98315cf5530d" 
                alt="Coleese Network Graph" 
                className="w-full h-auto object-cover scale-100 group-hover:scale-105 transition-transform duration-[4s]"
                referrerPolicy="no-referrer"
            />
          </div>
        </div>
      </section>

      {/* 
        VETTING SECTION 
      */}
      <section className="py-14 lg:py-48 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-20">
          <div className="flex-1 flex flex-col items-center lg:items-start">
            <h2 className="text-[32px] lg:text-[72px] font-black tracking-tight leading-[1.2] lg:leading-[1] mb-8 text-center lg:text-left">
              Know your tenant. Vetting simplified
            </h2>
            <p className="text-base lg:text-xl text-[#646A71] mb-10 max-w-xl font-medium leading-relaxed text-center lg:text-left">
              Instantly vet applicants and secure leases with Coleese. Move fast and fill vacancies in minutes, not weeks, wherever your property is.
            </p>
            <button onClick={onJoinWaitingList} className="px-8 py-4 rounded-full bg-[#191C1F] text-white font-bold text-sm lg:text-base">
               Join waiting list
            </button>
          </div>

          <div className="flex-1 relative">
            <div className="relative rounded-[24px] overflow-hidden shadow-[0_18px_100px_-20px_rgba(0,0,0,0.3)]">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2Fpexels-ali-moradi-167146005-19662526.jpg?alt=media&token=4201cb24-e3ee-402d-a7e9-6b1d86fae851" 
                alt="Applicant Vetting" 
                className="w-full h-[500px] lg:h-[600px] object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
              
              {/* Applicant Overview Card Styled UI */}
              <motion.div 
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="absolute bottom-10 right-[-20px] lg:right-[-40px] w-full max-w-[340px] bg-white/80 backdrop-blur-xl rounded-3xl p-6 text-black shadow-2xl border border-gray-100 flex flex-col justify-between h-[290px]"
              >
                <div>
                   <div className="flex items-center justify-between pb-3 border-b mb-4">
                       <div className="flex items-center gap-3">
                           <img 
                               src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80" 
                               className="w-10 h-10 lg:w-12 lg:h-12 rounded-full border-2 border-white/20 object-cover"
                               alt="Applicant"
                           />
                           <div className="flex flex-col items-start">
                               <h1 className="text-sm lg:text-base font-bold tracking-tight text-gray-900">Sarah M.</h1>
                               <p className="font-bold text-[10px] uppercase tracking-wide text-gray-400">Applicant Overview</p>
                           </div>
                       </div>
                   </div>

                   <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                       <div>
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Pass Score</p>
                           <div className="text-lg font-black mt-1">94 / 100</div>
                       </div>
                       <div>
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Rent Coverage</p>
                           <div className="flex items-baseline gap-1 mt-1">
                               <p className="text-lg font-bold text-gray-900">3.2x</p>
                               <span className="text-[10px] font-bold text-green-600">(31%)</span>
                           </div>
                       </div>
                       <div>
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Employment</p>
                           <div className="flex items-center gap-1.5 mt-1">
                               <Building2 size={12} className="text-gray-400" />
                               <p className="text-xs font-bold text-gray-900 truncate">Software Engineer</p>
                           </div>
                       </div>
                       <div>
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">ID Status</p>
                           <div className="flex items-center gap-1.5 mt-1">
                               <Shield size={12} className="text-green-500" />
                               <p className="text-xs font-bold text-green-700">Verified</p>
                           </div>
                       </div>
                   </div>
                </div>

                <div className="flex gap-2 pt-4 mt-auto">
                   <div className="flex-1 bg-gray-100 text-gray-700 font-bold py-2 rounded-xl text-[10px] flex items-center justify-center gap-1.5">
                      <FileText size={14} /> View Docs
                   </div>
                   <div className="flex-1 bg-black text-white font-bold py-2 rounded-xl text-[10px] flex items-center justify-center gap-1.5">
                      <MessageSquare size={14} className="fill-white" /> Message
                   </div>
                </div>
              </motion.div>
          </div>
        </div>
      </section>

      {/* 
        HOW TO HOST SMARTER SECTION - Redesigned to match Tenant Page 
      */}
      <section className="py-10 lg:py-32 px-6 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-end justify-between gap-8 mb-16 px-4 sm:px-0">
              <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
                  <h2 className="text-[32px] lg:text-[72px] font-black tracking-tight leading-none mb-6">
                      Four ways to host smarter
                  </h2>
                  <p className="text-[#646A71] text-lg lg:text-xl font-medium">
                      One account for all your property management needs.
                  </p>
              </div>
              <button 
                  onClick={onJoinWaitingList}
                  className="hidden lg:flex items-center gap-2 bg-black text-white px-8 py-4 rounded-full font-bold hover:bg-gray-800 transition-all shadow-lg active:scale-95"
              >
                  Get access <ChevronRight size={20} />
              </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                title: 'Auto-collection', 
                desc: 'Rent collection handled as soon as it leaves the tenant account.', 
                icon: <Landmark className="text-white" />,
                feature: 'Synchronous',
                bg: 'bg-black'
              },
              { 
                title: 'Digital Lease', 
                desc: 'Secure, legally binding contracts signed in seconds on any device.', 
                icon: <FileText className="text-white" />,
                feature: 'Paperless',
                bg: 'bg-[#2088ff]'
              },
              { 
                title: 'Smart Vetting', 
                desc: 'AI-driven applicant scoring for instant, reliable decisions.', 
                icon: <Shield className="text-white" />,
                feature: 'Instant',
                bg: 'bg-gradient-to-tr from-[#fd297b] to-[#ff655b]'
              },
              { 
                title: 'Full Control', 
                desc: 'Dashboard viewing 24/7 for yields and vacancies.', 
                icon: <Wallet className="text-white" />,
                feature: 'Real-time',
                bg: 'bg-[#f47c20]'
              },
            ].map((card, idx) => (
              <div key={idx} className="bg-white p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all cursor-pointer group flex flex-col h-full">
                <div className={`w-10 h-10 flex items-center justify-center mb-8 ${card.bg} rounded-full transition-colors`}>
                  {card.icon}
                </div>
                <div className="flex-1">
                   <h3 className="text-2xl font-black mb-3 leading-tight">{card.title}</h3>
                   <p className="text-[#646A71] font-medium leading-relaxed text-sm mb-6">{card.desc}</p>
                </div>
                <div className="pt-4 border-t border-gray-50 flex items-center justify-between mt-auto">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{card.feature}</span>
                    <ArrowRight size={18} className="text-gray-300 group-hover:text-black transition-colors" />
                </div>
              </div>
            ))}
          </div>

          <button 
              onClick={onJoinWaitingList}
              className="lg:hidden w-full mt-12 bg-black text-white text-sm px-8 py-4 rounded-full font-bold hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
          >
              Get access <ChevronRight size={20} />
          </button>
        </div>
      </section>

      <section className="pt-16 sm:pt-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto pb-8 lg:pb-14 px-6">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight">Self Managed Hosting</h2>
            <p className="text-sm lg:text-base mt-4">Simple, secure, and built for you to manage your rental your way.</p>
          </div>
          <div className="bg-gray-100 rounded-3xl p-8 md:p-12 lg:p-16 flex flex-col md:flex-row items-start gap-8 md:gap-16">
            <div className="md:w-2/3 text-center md:text-left md:pt-16">
              <SelfManagedHosting />
              <div className="mt-8 text-sm text-gray-500 text-left">
                ** A monthly platform fee of R399 will be charged for each active lease
              </div>
            </div>
            <div className="md:w-1/3 w-full">
                <div className="flex justify-center mb-6">
                    <div className="bg-gray-200 p-1 rounded-full flex items-center space-x-1">
                        <button 
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-6 py-2 text-sm font-semibold rounded-full transition-colors ${billingCycle === 'monthly' ? 'bg-white shadow' : 'text-gray-600'}`}
                        >
                            Monthly
                        </button>
                        <button 
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-6 py-2 text-sm font-semibold rounded-full transition-colors relative ${billingCycle === 'yearly' ? 'bg-white shadow' : 'text-gray-600'}`}
                        >
                            Annually
                            {plans.yearly.badge && <span className="absolute -top-2 -right-2 text-xs bg-lime-400 text-black font-bold px-2 py-0.5 rounded-full">{plans.yearly.badge}</span>}
                        </button>
                    </div>
                </div>
                <div className="p-8 border rounded-3xl flex flex-col h-full bg-black text-white border-black">
                    <h3 className="text-2xl font-bold mt-4">{selectedPlan.name}</h3>
                    <p className="mt-2 flex-grow text-gray-300">{selectedPlan.description}</p>
                    <div className="my-8">
                        <span className="text-5xl font-bold">R{selectedPlan.price}</span>
                        <span className="ml-2 text-gray-400">/month</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                        {selectedPlan.features.map((feature: string) => (
                            <li key={feature} className="flex items-center">
                                <CheckCircle size={16} className="text-green-500 mr-3 flex-shrink-0" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                    <button onClick={onJoinWaitingList} className="w-full mt-auto font-bold py-3 rounded-lg transition-colors bg-white text-black hover:bg-gray-200">
                        Get access
                    </button>
                </div>
            </div>
          </div>
        </div>
      </section>

      <FaqSection />
    </div>
  );
};